import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LogIn, LogOut, Camera, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate } from 'react-router-dom';

const API = '/api';

export default function AccessiMobile() {
  const navigate = useNavigate();
  const [tipoAccesso, setTipoAccesso] = useState('entrata');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ entrate: 0, uscite: 0, presenti: 0 });
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => {
      clearInterval(interval);
      stopScanner();
    };
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(API + '/reports/accessi', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStats({
          entrate: data.entrate_oggi || 0,
          uscite: data.uscite_oggi || 0,
          presenti: data.presenti_ora || 0
        });
      }
    } catch (e) { console.error(e); }
  };

  const startScanner = async () => {
    setError(null);
    setResult(null);
    setScanning(true);
    try {
      if (!html5QrRef.current) {
        html5QrRef.current = new Html5Qrcode('scanner-region');
      }
      await html5QrRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        onScanSuccess,
        () => {}
      );
    } catch (e) {
      setError('Errore fotocamera: ' + e.message);
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrRef.current && html5QrRef.current.isScanning) {
      try {
        await html5QrRef.current.stop();
      } catch (e) { console.error(e); }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    await stopScanner();
    try {
      const res = await fetch(API + '/accessi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          persona_id: decodedText,
          tipo_accesso: tipoAccesso,
          area_accesso: 'ingresso_principale'
        })
      });
      if (res.ok) {
        const data = await res.json();
        setResult({ success: true, message: tipoAccesso === 'entrata' ? 'ENTRATA registrata!' : 'USCITA registrata!', persona: data.persona_nome || decodedText });
        fetchStats();
      } else {
        const err = await res.json();
        setResult({ success: false, message: err.detail || 'Errore registrazione' });
      }
    } catch (e) {
      setResult({ success: false, message: 'Errore: ' + e.message });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-md mx-auto">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 mb-4">
          <ArrowLeft className="h-5 w-5" /> Torna al menu
        </button>
        
        <h1 className="text-2xl font-bold text-center mb-4">Controllo Accessi</h1>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-green-100 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-700">{stats.entrate}</div>
            <div className="text-xs text-green-600">Entrate</div>
          </div>
          <div className="bg-red-100 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-red-700">{stats.uscite}</div>
            <div className="text-xs text-red-600">Uscite</div>
          </div>
          <div className="bg-blue-100 p-3 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-700">{stats.presenti}</div>
            <div className="text-xs text-blue-600">Presenti</div>
          </div>
        </div>

        {/* Tipo Accesso */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <Button 
            onClick={() => setTipoAccesso('entrata')} 
            className={tipoAccesso === 'entrata' ? 'bg-green-600 hover:bg-green-700 h-16 text-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 h-16 text-lg'}
          >
            <LogIn className="h-6 w-6 mr-2" /> ENTRATA
          </Button>
          <Button 
            onClick={() => setTipoAccesso('uscita')} 
            className={tipoAccesso === 'uscita' ? 'bg-red-600 hover:bg-red-700 h-16 text-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300 h-16 text-lg'}
          >
            <LogOut className="h-6 w-6 mr-2" /> USCITA
          </Button>
        </div>

        {/* Scanner Area */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div id="scanner-region" ref={scannerRef} className="w-full min-h-[300px] bg-gray-900 rounded-lg overflow-hidden"></div>
            
            {!scanning && (
              <Button onClick={startScanner} className="w-full mt-4 h-14 text-lg bg-blue-600 hover:bg-blue-700">
                <Camera className="h-6 w-6 mr-2" /> Avvia Scansione
              </Button>
            )}
            {scanning && (
              <Button onClick={stopScanner} variant="destructive" className="w-full mt-4 h-14 text-lg">
                Ferma Scansione
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <Card className={result.success ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}>
            <CardContent className="p-4 text-center">
              {result.success ? (
                <CheckCircle className="h-12 w-12 mx-auto text-green-600 mb-2" />
              ) : (
                <XCircle className="h-12 w-12 mx-auto text-red-600 mb-2" />
              )}
              <div className={result.success ? 'text-green-800 text-xl font-bold' : 'text-red-800 text-xl font-bold'}>
                {result.message}
              </div>
              {result.persona && <div className="text-gray-600 mt-1">{result.persona}</div>}
              <Button onClick={() => { setResult(null); startScanner(); }} className="mt-4">
                Prossima Scansione
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-100 text-red-800 p-4 rounded-lg text-center">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
